/**
 * Tenant user management client (§7.1).
 *
 * Every call here requires TENANT_ADMIN server-side; the UI hides the entry point for anyone else,
 * but the server is what actually enforces it.
 */

import { AuthError } from './auth';
import { authorizedFetch } from './cognito';
import { Role } from './types';

const API_BASE = '/api/v1';

export interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  roles: Role[];
  status: 'ACTIVE' | 'DISABLED';
  last_seen_at?: string;
  can_read_phi: boolean;
}

/**
 * Every other client in the app goes through `authorizedFetch`; this one did not (§13.6 A8).
 *
 * That meant admin calls had no request timeout — so a stalled connection hung indefinitely with
 * no error — and no silent token refresh, so a token expiring mid-session redirected the tenant
 * admin through Cognito and lost whatever they were editing, in a screen where the rest of the
 * app would simply have carried on.
 */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authorizedFetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // `authorizedFetch` has already tried a silent refresh and, failing that, begun the redirect.
    throw new AuthError(401, 'Not signed in');
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') message = body.detail;
    } catch {
      /* keep the generic message */
    }
    throw new AuthError(res.status, message);
  }
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

export const fetchTenantUsers = () => call<TenantUser[]>('/tenant/users');

export const createTenantUser = (email: string, fullName: string, roles: Role[]) =>
  call<TenantUser>('/tenant/users', {
    method: 'POST',
    body: JSON.stringify({ email, full_name: fullName, roles }),
  });

export const updateTenantUserRoles = (id: string, roles: Role[]) =>
  call<TenantUser>(`/tenant/users/${id}`, { method: 'PATCH', body: JSON.stringify({ roles }) });

export const disableTenantUser = (id: string) =>
  call<TenantUser>(`/tenant/users/${id}/disable`, { method: 'POST' });

export const enableTenantUser = (id: string) =>
  call<TenantUser>(`/tenant/users/${id}/enable`, { method: 'POST' });

export const ALL_ROLES: Role[] = ['TENANT_ADMIN', 'TECHNICIAN', 'PHYSICIAN', 'CORPUS_REVIEWER'];

export const ROLE_LABEL: Record<Role, string> = {
  PLATFORM_OPS: 'Platform Ops',
  TENANT_ADMIN: 'Admin',
  TECHNICIAN: 'Technician',
  PHYSICIAN: 'Physician',
  CORPUS_REVIEWER: 'Corpus Reviewer',
  READ_ONLY: 'Read-only',
};
